import type { MatDialogRef } from '@angular/material/dialog';
import { qpxToast as toast } from '@quipex/core/utils';
import {
    ACTIVITY_DESTINATION_CLOSE_RESULT,
    focusActivityDestination,
} from '../activity-dialog/activity-dialog-result';
import type { ActivityIntent, ActivityKey } from '../activity-journal';
import { activityActionFailureMessage } from '../activity-presentation';
import type {
    ActivityActionResult,
    ActivityToastView,
} from '../activity-runtime/action-contract';
import type { ActivityToastPresentation } from '../activity-runtime/context';
import {
    activityToastLifecycle,
    type ActivityToastScope,
} from '../activity-runtime/toast-owner';
import type { ActivityLauncher } from './launcher';

const toastActionTypes = new Map<ActivityKey, ActivityIntent['type']>();
const PERSISTENT_ACTIVITY_TOAST_OPTIONS = {
    closeButton: true,
    dismissible: true,
    duration: Number.POSITIVE_INFINITY,
} as const;

export type ActivityDialogCloseResult =
    | false
    | undefined
    | typeof ACTIVITY_DESTINATION_CLOSE_RESULT;

export function showActivityToast(
    decision: ActivityToastPresentation,
    open: ActivityLauncher['open'],
    executeAction: (decision: ActivityToastView) => Promise<void>,
    scope: ActivityToastScope
): { readonly id: string; readonly persistent: boolean } {
    const action = decision.action;
    const persistent =
        action !== null ||
        decision.kind === 'error' ||
        decision.kind === 'warning';
    if (action) toastActionTypes.set(decision.key, action.type);
    else toastActionTypes.delete(decision.key);
    const toastId = `activity:${decision.key}`;
    const options = {
        id: toastId,
        ...activityToastLifecycle(scope),
        ...(persistent ? PERSISTENT_ACTIVITY_TOAST_OPTIONS : {}),
        ...(decision.description ? { description: decision.description } : {}),
        ...(action
            ? {
                  action: {
                      label: action.label,
                      onClick: scope.guard(() => {
                          scope.release();
                          void executeAction(decision);
                      }),
                  },
              }
            : {
                  action: {
                      label: 'View activity',
                      onClick: scope.guard(() => {
                          scope.release();
                          void open();
                      }),
                  },
              }),
    };

    if (decision.kind === 'success') {
        toast.success(decision.title, options);
    } else if (decision.kind === 'warning') {
        toast.warning(decision.title, options);
    } else {
        toast.error(decision.title, options);
    }
    scope.own(toastId);
    return { id: toastId, persistent };
}

export function handleToastActionResult(
    key: ActivityKey,
    result: ActivityActionResult,
    open: ActivityLauncher['open'],
    openDialogRef: MatDialogRef<unknown, ActivityDialogCloseResult> | undefined,
    document: Document,
    scope: ActivityToastScope,
    focusDestination: () => void
): boolean {
    const actionType = toastActionTypes.get(key);
    if (result === 'downloaded') {
        toastActionTypes.delete(key);
        openDialogRef?.close();
        return false;
    }
    if (result === 'navigated') {
        toastActionTypes.delete(key);
        if (openDialogRef) {
            openDialogRef.close(ACTIVITY_DESTINATION_CLOSE_RESULT);
        } else {
            document.defaultView?.setTimeout(() => {
                if (!focusTopmostDialog(document)) {
                    focusActivityDestination(document, focusDestination);
                }
            });
        }
        return false;
    }

    const toastId = `activity:action:${key}`;
    const lifecycle = activityToastLifecycle(scope);
    if (result === 'unavailable') {
        toast.error(
            'This activity is no longer available or you no longer have access.',
            {
                id: toastId,
                ...lifecycle,
                ...PERSISTENT_ACTIVITY_TOAST_OPTIONS,
            }
        );
    } else if (result === 'failed') {
        toast.error(activityActionFailureMessage(actionType), {
            id: toastId,
            ...lifecycle,
            ...PERSISTENT_ACTIVITY_TOAST_OPTIONS,
        });
    } else if (result === 'ignored') {
        toast.error(
            'This activity is no longer available. Review current background work in Activity.',
            {
                id: toastId,
                ...lifecycle,
                ...PERSISTENT_ACTIVITY_TOAST_OPTIONS,
                action: {
                    label: 'View activity',
                    onClick: scope.guard(() => {
                        scope.release();
                        void open();
                    }),
                },
            }
        );
    } else {
        return false;
    }
    toastActionTypes.delete(key);
    scope.own(toastId);
    return true;
}

export function getFocusOrigin(document: Document): HTMLElement | null {
    const activeElement = document.activeElement;
    const HTMLElementConstructor = document.defaultView?.HTMLElement;

    return HTMLElementConstructor &&
        activeElement instanceof HTMLElementConstructor
        ? activeElement
        : null;
}

export function restoreActivityFocus(
    focusOrigin: HTMLElement | null,
    focusFallback: () => void
): void {
    if (focusOrigin?.isConnected) {
        focusOrigin.focus();
        return;
    }

    focusFallback();
}

export function focusTopmostDialog(document: Document): boolean {
    const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"]');
    const topmostDialog = dialogs.item(dialogs.length - 1);
    if (!topmostDialog) return false;
    if (topmostDialog.contains(document.activeElement)) return true;

    const focusTarget = topmostDialog.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusTarget) {
        focusTarget.focus();
    } else {
        topmostDialog.setAttribute('tabindex', '-1');
        topmostDialog.focus();
    }
    return true;
}
